/**
 * Utility to interact with OpenRouter API
 */
export const callOpenRouter = async (apiKey, model, songInfo) => {
  const prompt = `
Analyze the following music reference:
Reference: ${songInfo}

Your task:
1. Identify the core musical characteristics (Genre, Mood, Instruments, Vocals, Structure).
2. Based on this analysis, generate 10 unique, high-quality prompts for Suno AI V5.
3. Each prompt MUST follow this exact format:
[Genre] ..., [Mood] ..., [Instruments] ..., [Vocals] ..., [Structure] ...

IMPORTANT: Respond ONLY with a valid JSON array of 10 strings. No conversational text.
  `;

  try {
    const response = await fetch("https://openrouter.ai/api/v1/chat/completions", {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${apiKey}`,
        "HTTP-Referer": window.location.origin,
        "X-Title": "SunoSync Pro",
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        "model": model || "openai/gpt-3.5-turbo",
        "messages": [
          { "role": "system", "content": "You are a professional music producer and Suno AI prompt engineer. You output only JSON arrays of strings." },
          { "role": "user", "content": prompt }
        ]
      })
    });

    const data = await response.json();
    if (data.error) throw new Error(data.error.message || "API Error");

    const content = data.choices[0].message.content;
    const cleanContent = content.replace(/```json/g, '').replace(/```/g, '').trim();
    
    return JSON.parse(cleanContent);
  } catch (error) {
    console.error("OpenRouter Error:", error);
    throw error;
  }
};

export const generateCreatorPrompt = async (apiKey, model, mode, description) => {
  const systemPrompt = `
You are an elite Suno V5.5 prompt engineer and expert lyricist. Your job is to convert the user's idea into the perfect Suno generation prompt based on strict mechanics.

MODES:
1. Simple: 3000 char max. No exclude prompt. Generates everything including lyrics.
2. Advanced: 1000 char max for style. Needs an exclude prompt. Needs full lyrics formatted with bracket tags.
3. Studio: 1000 char max. No genre tags allowed (describe sound only). Needs an exclude prompt to remove unwanted instruments. No lyrics.

FORMATTING RULES (Hybrid Format Preferred):
- Combine tag lists with light prose. Ex: "Hard rock at 142 BPM with crunchy power chords. Driving bassline locks in with punchy drums. Gritty baritone male vocal..."
- Order of info: Genre/Era -> BPM -> Mood -> Lead Inst -> Rhythm -> Vocals -> Production.
- Avoid contamination words unless explicitly requested: 'live', 'concert', 'acoustic' (forces acoustic guitar), 'arena', 'crowd'.

EXCLUDE PROMPT RULES (Advanced/Studio only):
- Use positive keywords only (e.g., "piano, synths, vocals" NOT "no piano").
- Target opposites of the requested vibe + genre-adjacent contamination.

LYRIC CRAFTING RULES (Advanced only - CRITICAL FOR MUSICAL STRUCTURE):
- SYLLABLE COUNTS: Match syllable counts within each section (within ±2). Vary syllable counts between different sections to dictate energy changes.
- RHYME SCHEMES: Vary between sections. Verses: ABAB, ABCB. Chorus: AABB, ABAB. Bridge: contrasting scheme. NEVER use AABB for both verse and chorus.
- LINE ENDINGS: Maintain consistent stress patterns (masculine/hard or feminine/soft endings) within a single section.
- SECTION LENGTHS & TRANSITIONS: Default to even line counts (4, 6, 8) for stability. Use an odd line count ONLY to deliberately disrupt structure and set up a [Drop] or major beat-switch.
- PUNCTUATION & TYPOGRAPHY: Use periods for strong stops, commas for open/flowing lines. Add a completely blank line within a section to force a musical pause.
- META-TAGS: Use Suno specialty tags inside brackets: [crescendo], [power-off drop], [beat-switch], [ad-lib], [choir: gospel], [modulation: sudden].

SONG TITLES (ALL MODES):
- Generate EXACTLY 5 creative song titles relevant to the description.
- THEY MUST NOT SOUND AI-GENERATED. Avoid generic clichés (e.g., "Neon Dreams", "Echoes of Tomorrow", "Whispering Winds", "Shadows of my Heart", "Symphony", "Chronicles").
- Use highly specific, grounded, conversational, or sensory-rich phrases that sound like real commercial or indie releases (e.g., "Burned the Bread", "Quarter to Three", "Rust on the Chrome").

Respond ONLY with a valid JSON object matching this exact structure:
{
  "stylePrompt": "The hybrid format prompt text...",
  "excludePrompt": "comma, separated, tags, to, avoid (leave empty if Simple mode)",
  "lyrics": "[Verse 1 | production cues]\\nLyric line 1\\nLyric line 2... (leave empty if Simple or Studio mode)",
  "titles": ["Specific Title 1", "Concrete Title 2", "Conversational Title 3", "Sensory Title 4", "Unexpected Title 5"]
}
  `;

  try {
    const response = await fetch("https://openrouter.ai/api/v1/chat/completions", {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${apiKey}`,
        "HTTP-Referer": window.location.origin,
        "X-Title": "SunoSync Pro",
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        "model": model || "openai/gpt-4o-mini",
        "response_format": { "type": "json_object" },
        "messages": [
          { "role": "system", "content": systemPrompt },
          { "role": "user", "content": `Mode: ${mode}\nUser Description: ${description}` }
        ]
      })
    });

    const data = await response.json();
    if (data.error) throw new Error(data.error.message || "API Error");

    return JSON.parse(data.choices[0].message.content);
  } catch (error) {
    console.error("OpenRouter Error:", error);
    throw error;
  }
};